// src/components/games/pipsCreatureKeeper/HabitatGrid.tsx
import { logger } from '../../../utils/logger';

import React, { useState, useMemo, useCallback } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonGrid,
  IonRow,
  IonCol,
  IonButton,
  IonBadge,
  IonAlert,
  IonIcon
} from '@ionic/react';
import { trash, star, starOutline, chevronBack, chevronForward, add } from 'ionicons/icons';
import { Creature, PlayerCreature, Habitat, TypeCompatibility, HabitatPlacement } from '../../../types/pipsCreatureKeeper';
import { updateCreaturePlacement, setExplorationCompanion, releaseCreature } from '../../../services/pipsCreatureKeeperService';
import { supabase } from '../../../services/supabase';
import { soundService } from '../../../services/soundService';
import VictoryScreen from './VictoryScreen';
import './HabitatGrid.css';

interface HabitatGridProps {
  habitats: Habitat[];
  unlockedHabitats: number[];
  habitatPlacements: { [habitatId: number]: HabitatPlacement[] };
  myCreatures: PlayerCreature[];
  creatures: Creature[];
  typeCompatibility: TypeCompatibility[];
  selectedCompanion: Creature | null;
  onCompanionSelect: (creature: Creature | null) => void;
  onRefresh: () => void;
  onNavigateToExplore: () => void;
  selectedHabitatIndex?: number;
  onHabitatIndexChange?: (index: number) => void;
}

const TYPE_COLORS: { [key: string]: string } = {
  flame: '#FF4500',
  tide: '#1E90FF',
  stone: '#696969',
  storm: '#FFA500',
  frost: '#87CEEB',
  bloom: '#32CD32',
  shadow: '#2F2F2F',
  light: '#FFFFE0',
  metal: '#C0C0C0',
  spirit: '#9370DB'
};

const getTypeColor = (type: string): string => {
  return TYPE_COLORS[type.toLowerCase()] || '#999';
};

const getTypeTextColor = (type: string): string => {
  const darkTypes = ['stone', 'shadow', 'spirit', 'flame'];
  return darkTypes.includes(type.toLowerCase()) ? '#fff' : '#000';
};

const getHappinessIcon = (happiness: number) => {
  const h = happiness || 50;
  if (h >= 60) return '😊 ';
  if (h >= 40) return '😐';
  return '😞';
};

const HabitatGrid: React.FC<HabitatGridProps> = ({
  habitats,
  unlockedHabitats,
  habitatPlacements,
  myCreatures,
  creatures,
  typeCompatibility,
  selectedCompanion,
  onCompanionSelect,
  onRefresh,
  onNavigateToExplore,
  selectedHabitatIndex = 0,
  onHabitatIndexChange
}) => {
  const [selectedCreature, setSelectedCreature] = useState<PlayerCreature | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [showReleaseAlert, setShowReleaseAlert] = useState<{ show: boolean; creature: PlayerCreature | null }>({
    show: false,
    creature: null
  });
  const [showVictoryScreen, setShowVictoryScreen] = useState(false);
  const [keeperName, setKeeperName] = useState('Keeper');

  // Create a map for quick creature lookup
  const creatureMap = useMemo(() => {
    const map = new Map<number, Creature>();
    creatures.forEach(c => map.set(c.id, c));
    return map;
  }, [creatures]);

  const getCreatureById = useCallback((id: number): Creature | undefined => {
    return creatureMap.get(id);
  }, [creatureMap]);

  // Get unplaced creatures
  const unplacedCreatures = useMemo(() => {
    const placedIds = new Set<string>();
    Object.values(habitatPlacements).forEach(habitat => {
      habitat.forEach(creature => placedIds.add(creature.id));
    });
    return myCreatures.filter(pc => !placedIds.has(pc.id));
  }, [myCreatures, habitatPlacements]);

  // Get unlocked habitat data
  const unlockedHabitatData = useMemo(() => {
    return habitats.filter(h => unlockedHabitats.includes(h.id));
  }, [habitats, unlockedHabitats]);

  const currentHabitat = unlockedHabitatData[selectedHabitatIndex];

  // Load keeper name
  React.useEffect(() => {
    const loadKeeperName = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: game } = await supabase
          .from('creature_catcher_games')
          .select('player_name')
          .eq('user_id', user.id)
          .single();

        if (game?.player_name) {
          setKeeperName(game.player_name);
        }
      } catch (error) {
        logger.error('Error loading keeper name:', error);
      }
    };

    loadKeeperName();
  }, []);

  // Check for victory condition
  React.useEffect(() => {
    // Victory condition: All 50 creatures are placed in habitats (none unplaced)
    const allCreaturesPlaced = myCreatures.length === 50 && unplacedCreatures.length === 0;

    if (allCreaturesPlaced && !showVictoryScreen) {
      logger.log('[VICTORY] All 50 creatures captured and placed!');
      // Small delay so the user can see the placement happen
      setTimeout(() => {
        setShowVictoryScreen(true);
      }, 500);
    }
  }, [myCreatures.length, unplacedCreatures.length, showVictoryScreen]);

  // Handlers
  const handleHabitatIndexChange = useCallback((newIndex: number) => {
    if (onHabitatIndexChange) {
      onHabitatIndexChange(newIndex);
    }
  }, [onHabitatIndexChange]);

  const handleCreatureClick = useCallback((creature: PlayerCreature) => {
    const newSelection = selectedCreature?.id === creature.id ? null : creature;
    logger.log('[Creature Click]', creature.id, 'New selection:', newSelection?.id || 'none');
    setSelectedCreature(newSelection);
  }, [selectedCreature]);

  const handlePlaceCreature = useCallback(async (habitatId: number, slot: number) => {
    logger.log('[Place Creature] Called:', { habitatId, slot, selectedCreature: selectedCreature?.id, placing });

    if (!selectedCreature) {
      logger.warn('[Place Creature] Cannot place: no creature selected');
      return;
    }

    if (placing) {
      logger.warn('[Place Creature] Cannot place: already placing');
      return;
    }

    setPlacing(true);
    logger.log('[Place Creature] Placing:', selectedCreature.id, 'in habitat:', habitatId, 'slot:', slot);

    try {
      await updateCreaturePlacement(selectedCreature.id, habitatId, slot);
      logger.log('[Place Creature] Success: creature placed');

      // Play placement sound
      soundService.play('habitat-place', { volume: 0.6, haptic: 'light' });

      await onRefresh();
      logger.log('[Place Creature] Success: data refreshed');
      setSelectedCreature(null);
    } catch (error) {
      logger.error('[Place Creature] Error:', error);
    } finally {
      setPlacing(false);
      logger.log('[Place Creature] State reset');
    }
  }, [selectedCreature, placing, onRefresh]);

  const handleUnplaceCreature = useCallback(async () => {
    if (!selectedCreature || placing) return;

    setPlacing(true);
    try {
      await updateCreaturePlacement(selectedCreature.id, null as any, null as any);
      await onRefresh();
      setSelectedCreature(null);
    } catch (error) {
      logger.error('Error unplacing creature:', error);
    } finally {
      setPlacing(false);
    }
  }, [selectedCreature, placing, onRefresh]);

  const handleSetCompanion = useCallback(async (playerCreature: PlayerCreature, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const creatureData = getCreatureById(playerCreature.creature_id);
      if (!creatureData) return;

      const isCurrentCompanion = playerCreature.is_exploring;

      if (isCurrentCompanion) {
        await setExplorationCompanion(playerCreature.game_id, null);
        onCompanionSelect(null);
      } else {
        await setExplorationCompanion(playerCreature.game_id, playerCreature.id);
        onCompanionSelect(creatureData);
      }

      await onRefresh();
    } catch (error) {
      logger.error('Error setting companion:', error);
    }
  }, [getCreatureById, onCompanionSelect, onRefresh]);

  const handleReleaseClick = useCallback((creature: PlayerCreature, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReleaseAlert({ show: true, creature });
  }, []);

  const handleConfirmRelease = useCallback(async () => {
    if (!showReleaseAlert.creature || releasing) return;

    setReleasing(true);
    try {
      await releaseCreature(showReleaseAlert.creature.id);
      setShowReleaseAlert({ show: false, creature: null });
      setSelectedCreature(null);
      await onRefresh();
    } catch (error) {
      logger.error('Error releasing creature:', error);
    } finally {
      setReleasing(false);
    }
  }, [showReleaseAlert.creature, releasing, onRefresh]);

  const isCreatureCompanion = useCallback((playerCreature: PlayerCreature): boolean => {
    return playerCreature.is_exploring === true;
  }, []);

  const handlePrevHabitat = () => {
    const newIndex = Math.max(0, selectedHabitatIndex - 1);
    handleHabitatIndexChange(newIndex);
  };

  const handleNextHabitat = () => {
    const newIndex = Math.min(unlockedHabitatData.length - 1, selectedHabitatIndex + 1);
    handleHabitatIndexChange(newIndex);
  };

  // Render creature card
  const renderCreatureCard = useCallback((playerCreature: PlayerCreature, inHabitat: boolean) => {
    const creature = getCreatureById(playerCreature.creature_id);
    if (!creature) return null;

    const isSelected = selectedCreature?.id === playerCreature.id;
    const isCompanion = isCreatureCompanion(playerCreature);
    const happiness = (playerCreature as any).current_happiness || 70;

    return (
      <IonCard
        key={playerCreature.id}
        onClick={() => handleCreatureClick(playerCreature)}
        className={`habitat-creature-card ${isSelected ? 'selected' : ''} ${inHabitat ? 'in-habitat' : 'unplaced'}`}
      >
        <IonCardContent className="habitat-creature-content">
          <div className="creature-image-container">
            {creature.ascii_art ? (
              <pre className="ascii-art-small">
                {creature.ascii_art}
              </pre>
            ) : (
              <span className="placeholder-icon">🦋</span>
            )}
          </div>

          <div className="creature-name">
            {creature.name}
          </div>

          <div className="creature-types">
            <IonBadge
              style={{
                '--background': getTypeColor(creature.type1),
                color: getTypeTextColor(creature.type1)
              }}
              className="type-badge-small"
            >
              {creature.type1}
            </IonBadge>
            {creature.type2 && (
              <IonBadge
                style={{
                  '--background': getTypeColor(creature.type2),
                  color: getTypeTextColor(creature.type2)
                }}
                className="type-badge-small"
              >
                {creature.type2}
              </IonBadge>
            )}
          </div>

          {inHabitat && (
            <>
              <div className="creature-happiness">
                {getHappinessIcon(happiness)} {Math.round(happiness)}%
              </div>

              <div className="creature-actions">
                <IonButton
                  size="small"
                  fill="clear"
                  color={isCompanion ? 'warning' : 'medium'}
                  onClick={(e) => handleSetCompanion(playerCreature, e)}
                  className="action-button"
                  title={isCompanion ? 'Current companion' : 'Set as companion'}
                >
                  <IonIcon
                    slot="icon-only"
                    icon={isCompanion ? star : starOutline}
                    className={isCompanion ? 'companion-icon active' : 'companion-icon'}
                  />
                </IonButton>
                <IonButton
                  size="small"
                  fill="clear"
                  color="danger"
                  onClick={(e) => handleReleaseClick(playerCreature, e)}
                  className="action-button"
                >
                  <IonIcon slot="icon-only" icon={trash} />
                </IonButton>
              </div>
            </>
          )}
        </IonCardContent>
      </IonCard>
    );
  }, [selectedCreature, getCreatureById, isCreatureCompanion, handleCreatureClick, handleSetCompanion, handleReleaseClick]);

  // Render empty slot
  const renderEmptySlot = useCallback((slotIndex: number) => {
    return (
      <IonCard
        key={`empty-${slotIndex}`}
        button
        disabled={!selectedCreature || placing}
        onClick={() => selectedCreature && handlePlaceCreature(currentHabitat.id, slotIndex)}
        className={`empty-slot ${selectedCreature ? 'selectable' : ''}`}
      >
        <IonCardContent className="empty-slot-content">
          {selectedCreature ? (
            <>
              <IonIcon icon={add} className="add-icon active" />
              <div className="empty-slot-text active">
                Place Here
              </div>
            </>
          ) : (
            <>
              <div className="empty-slot-icon">🏕</div>
              <div className="empty-slot-text">
                Empty
              </div>
            </>
          )}
        </IonCardContent>
      </IonCard>
    );
  }, [selectedCreature, placing, currentHabitat, handlePlaceCreature]);

  // Early return if no habitat
  if (!currentHabitat) {
    return (
      <div className="habitat-grid-container ion-padding">
        <IonCard>
          <IonCardContent>
            <p className="no-habitat-message">No habitats unlocked yet!</p>
          </IonCardContent>
        </IonCard>
      </div>
    );
  }

  // Get habitat creatures and capacity
  const habitatCreatures = habitatPlacements[currentHabitat.id] || [];
  const capacity = currentHabitat.max_creatures;
  const occupied = habitatCreatures.length;

  // Sort creatures by slot
  const sortedCreatures = [...habitatCreatures].sort((a, b) => {
    const slotA = myCreatures.find(mc => mc.id === a.id)?.habitat_slot ?? 999;
    const slotB = myCreatures.find(mc => mc.id === b.id)?.habitat_slot ?? 999;
    return slotA - slotB;
  });

  // Build slot array with proper typing
  type SlotData = { type: 'creature'; data: PlayerCreature } | { type: 'empty'; slotIndex: number };
  const allSlots: SlotData[] = [];

  for (let i = 0; i < capacity; i++) {
    const creatureInSlot = sortedCreatures[i];
    if (creatureInSlot) {
      const playerCreature = myCreatures.find(mc => mc.id === creatureInSlot.id);
      if (playerCreature) {
        allSlots.push({ type: 'creature', data: playerCreature });
      } else {
        allSlots.push({ type: 'empty', slotIndex: i });
      }
    } else {
      allSlots.push({ type: 'empty', slotIndex: i });
    }
  }

  return (
    <div className="habitat-grid-container ion-padding">
      {/* Habitat navigation */}
      <IonCard className="habitat-nav-card">
        <IonCardHeader className="habitat-nav-header">
          <div className="habitat-nav-controls">
            <IonButton
              fill="clear"
              onClick={handlePrevHabitat}
              disabled={selectedHabitatIndex === 0}
              className="nav-button"
            >
              <IonIcon icon={chevronBack} />
            </IonButton>
            <IonCardTitle className="habitat-title">
              {currentHabitat.name}
            </IonCardTitle>
            <IonButton
              fill="clear"
              onClick={handleNextHabitat}
              disabled={selectedHabitatIndex === unlockedHabitatData.length - 1}
              className="nav-button"
            >
              <IonIcon icon={chevronForward} />
            </IonButton>
          </div>
        </IonCardHeader>
        <IonCardContent className="habitat-nav-content">
          <div className="habitat-capacity">
            Capacity: {occupied}/{capacity}
          </div>
          {selectedCreature && (
            <div className="habitat-hint active">
              Click an empty slot to place creature
            </div>
          )}
        </IonCardContent>
      </IonCard>

      {/* Habitat slots */}
      <IonCard className="habitat-slots-card">
        <IonCardHeader className="slots-header">
          <IonCardTitle className="section-title">Habitat Slots</IonCardTitle>
        </IonCardHeader>
        <IonCardContent className="slots-content">
          <IonGrid>
            <IonRow>
              {allSlots.map((slot, idx) => (
                <IonCol size="6" sizeMd="4" key={idx}>
                  {slot.type === 'creature'
                    ? renderCreatureCard(slot.data, true)
                    : renderEmptySlot(slot.slotIndex)
                  }
                </IonCol>
              ))}
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonCard>

      {/* Available Creatures - Always shown with storage slot */}
      <IonCard className="available-creatures-card">
        <IonCardHeader className="available-header">
          <IonCardTitle className="section-title">
            Available Creatures{unplacedCreatures.length > 0 ? ` (${unplacedCreatures.length})` : ''}
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent className="available-content">
          <p className="available-hint">
            {unplacedCreatures.length > 0
              ? 'Select a creature, then click an empty slot above to place it. You cannot explore while creatures are here.'
              : 'Temporary storage for rearranging creatures between habitats.'
            }
          </p>
          <IonGrid>
            <IonRow>
              {unplacedCreatures.map((creature) => (
                <IonCol size="6" sizeMd="4" key={creature.id}>
                  {renderCreatureCard(creature, false)}
                </IonCol>
              ))}
              {/* Always show at least one empty slot for temporary storage */}
              <IonCol size="6" sizeMd="4">
                <IonCard
                  button={!!selectedCreature}
                  disabled={!selectedCreature || placing}
                  onClick={() => selectedCreature && handleUnplaceCreature()}
                  className={`storage-slot ${selectedCreature ? 'selectable' : ''}`}
                >
                  <IonCardContent className="storage-slot-content">
                    {selectedCreature ? (
                      <>
                        <IonIcon icon={add} className="add-icon active" />
                        <div className="storage-slot-text active">
                          Move Here
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="storage-slot-icon">🦜</div>
                        <div className="storage-slot-text">
                          Storage
                        </div>
                      </>
                    )}
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCardContent>
      </IonCard>

      {/* Release confirmation alert */}
      <IonAlert
        isOpen={showReleaseAlert.show}
        onDidDismiss={() => setShowReleaseAlert({ show: false, creature: null })}
        header="Release Creature?"
        message={`Are you sure you want to release ${showReleaseAlert.creature?.creatures?.name || 'this creature'}? It will return to the wild.`}
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            handler: () => setShowReleaseAlert({ show: false, creature: null })
          },
          {
            text: 'Release',
            role: 'destructive',
            handler: handleConfirmRelease
          }
        ]}
      />

      {/* Victory Screen */}
      {showVictoryScreen && (
        <VictoryScreen
          keeperName={keeperName}
          totalCreatures={50}
          onClose={() => setShowVictoryScreen(false)}
        />
      )}
    </div>
  );
};

export default HabitatGrid;